<template>
  <div v-if="post">
    <!-- Start Header Area -->
    <Header>
      <img slot="logo" :src="logo" />
    </Header>
    <!-- End Header Area -->

    <!-- Start Breadcrump Area  -->
    <div
      class="rn-page-title-area pt--120 pb--190 bg_image"
      data-black-overlay="7"
      :style="{ backgroundImage: 'url(' + post.heroImage + ')' }"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <div class="text-center blog-single-page-title pt--100">
              <h2 class="heading-title">
                {{ post.title }}
              </h2>
              <ul class="justify-center blog-meta d-flex align-center">
                <li class="d-flex">
                  <span v-html="iconSvg('clock')"></span>{{ post.date }}
                </li>
                <li class="d-flex">
                  <span v-html="iconSvg('user')"></span>{{ post.author }}
                </li>
                <li class="d-flex">
                  <span v-html="iconSvg('clock')"></span>{{ post.readingTime }}
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <!-- End Breadcrump Area  -->

    <!-- Start Blog Details Area  -->
    <div class="rn-blog-details pt--110 pb--70 bg_color--1">
      <v-container>
        <v-row>
          <v-col cols="12">
            <div class="inner-wrapper">
              <div class="inner markdown-content" v-html="htmlContent"></div>
              
              <!-- Source citation if present -->
              <p v-if="frontmatter.source" class="mt--25 source-note">
                Fonte: <a :href="frontmatter.source" target="_blank" rel="noopener">{{ frontmatter.sourceTitle || 'Ver fonte' }}</a>
              </p>
              
              <!-- WhatsApp CTA if enabled -->
              <div v-if="frontmatter.whatsappCTA" class="mt--30" style="padding: 0 110px;">
                <a :href="whatsLink" class="footer-cta-whatsapp" target="_blank" rel="noopener" aria-label="Chamar no WhatsApp">
                  <i class="mdi mdi-whatsapp"></i>
                  <span class="cta-text">Entre em contato conosco</span>
                </a>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <!-- End Blog Details Area  -->

    <Footer />
  </div>
  
  <!-- Loading/Not Found State -->
  <div v-else class="rn-page-title-area pt--120 pb--190 bg_image bg_image--27" data-black-overlay="7">
    <v-container>
      <v-row>
        <v-col cols="12">
          <div class="text-center blog-single-page-title pt--100">
            <h2 class="heading-title theme-gradient">
              {{ loading ? 'Carregando...' : 'Artigo não encontrado' }}
            </h2>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import feather from "feather-icons";
import { marked } from "marked";

// Configure marked for consistent rendering
marked.setOptions({
  breaks: true,
  gfm: true
});

// Simple frontmatter parser (replaces gray-matter for browser)
function parseFrontmatter(content) {
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);
  
  if (!match) {
    return { data: {}, content };
  }
  
  const frontmatterStr = match[1];
  const bodyContent = match[2];
  const data = {};
  
  // Parse YAML-like frontmatter
  frontmatterStr.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      // Handle boolean values
      if (value === 'true') value = true;
      if (value === 'false') value = false;
      data[key] = value;
    }
  });
  
  return { data, content: bodyContent };
}

// Fetch post by slug from JSON
async function fetchPostBySlug(slug) {
  try {
    const response = await fetch('/content/blog/posts.json');
    const posts = await response.json();
    return posts.find(post => post.slug === slug) || null;
  } catch (error) {
    console.error('Error fetching posts:', error);
    return null;
  }
}

export default {
  components: {
    Header,
    Footer,
  },
  data() {
    return {
      logo: "/logoCodetimeWhite.webp",
      post: null,
      htmlContent: "",
      frontmatter: {},
      loading: true,
      whatsLink: "https://wa.me/5547933807051",
    };
  },
  created() {
    this.loadPost();
  },
  watch: {
    "$route.params.slug"() {
      this.loadPost();
    },
  },
  methods: {
    iconSvg(icon) {
      return feather.icons[icon].toSvg();
    },
    async loadPost() {
      this.loading = true;
      const slug = this.$route.params.slug;
      
      // Get post metadata from JSON
      this.post = await fetchPostBySlug(slug);
      
      if (!this.post) {
        this.loading = false;
        return;
      }
      
      // Update document title
      document.title = `${this.post.title} | Codetime`;
      
      try {
        // Fetch markdown content
        const response = await fetch(`/content/blog/${slug}.md`);
        if (!response.ok) throw new Error('Post not found');
        
        const markdown = await response.text();
        
        // Parse frontmatter and content using our custom parser
        const { data, content } = parseFrontmatter(markdown);
        this.frontmatter = data;
        
        // Convert markdown to HTML
        this.htmlContent = marked.parse(content);
        
      } catch (error) {
        console.error('Error loading post:', error);
        // Keep showing the post with just metadata, no content
        this.htmlContent = '<p>Conteúdo não disponível.</p>';
      }
      
      this.loading = false;
    },
  },
};
</script>

<style>
.source-note {
  font-size: 14px !important;
  color: #7a7a7a !important;
  opacity: 0.7 !important;
  padding: 0 110px;
}
.source-note a {
  color: inherit !important;
  text-decoration: underline !important;
}

/* Markdown content styling - matches original blog design */
.markdown-content p {
  margin-bottom: 1.5em;
}

.markdown-content h2,
.markdown-content h3,
.markdown-content h4 {
  margin-top: 2em;
  margin-bottom: 1em;
}

.markdown-content h2 {
  font-size: 1.5em;
}

.markdown-content h3 {
  font-size: 1.3em;
}

.markdown-content h4 {
  font-size: 1.1em;
}

.markdown-content ul,
.markdown-content ol {
  margin-bottom: 1.5em;
  padding-left: 1.5em;
}

.markdown-content li {
  margin-bottom: 0.5em;
  position: relative;
  padding-left: 5px;
}

.markdown-content ul li::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 100%;
  background: currentColor;
  position: absolute;
  left: -15px;
  top: 10px;
  opacity: 0.4;
}

.markdown-content blockquote {
  background: rgba(249, 0, 77, 0.05);
  border-left: 4px solid #f9004d;
  padding: 20px 30px;
  margin: 30px 0;
  font-style: italic;
  font-size: 1.1em;
}

.markdown-content img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 20px 0;
}

.markdown-content img + em {
  display: block;
  text-align: center;
  font-size: 0.9em;
  color: #888;
  margin-top: -10px;
  margin-bottom: 20px;
}

.markdown-content strong {
  font-weight: 600;
}

.markdown-content a {
  color: #f9004d;
  text-decoration: none;
}

.markdown-content a:hover {
  text-decoration: underline;
}

/* Dark mode adjustments */
.active-dark .markdown-content blockquote {
  background: rgba(249, 0, 77, 0.1);
}
</style>
